import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../utils';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const BuyPackage = () => {
  const navigate = useNavigate();
  const userData = useSelector(state => state.user_info[0]?.user);
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      fullName: userData.fullName,
      phone: userData.phone,
      sponsorCode: userData.sponsorCode,
      package: '',
    }
  });
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await sendApiRequest('api/packages', {}, 'GET');
        setPackages(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        toast.error('Failed to fetch packages');
        setPackages([]);
      }
    };

    const fetchCustomers = async () => {
      try {
        const response = await sendApiRequest('api/customers', {}, 'GET');
        setCustomers(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        toast.error('Failed to fetch packages');
        setCustomers([]);
      }
    };

    fetchCustomers();
    fetchPackages();
  }, []);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const response = await sendApiRequest('api/packages/buy', {
        package: data.package,
        customer: data.customer,
      }, 'POST');

      if (response.data.success) {
        toast.success('Package purchase request submitted successfully');
        navigate('/dashboard');
      } else {
        toast.error(response.message || 'Failed to purchase package');
      }
    } catch (error) {
      console.error('Error purchasing package:', error);
      toast.error('Failed to purchase package');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#49c2c7]/5 via-[#25b2da]/10 to-[#0d61b5]/5 py-8">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#25b2da]/20">
          <div className="bg-gradient-to-r from-[#13407e] to-[#0d61b5] text-white px-6 py-4">
            <h1 className="text-xl font-semibold">Buy Package</h1>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Personal Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData.fullName}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  Mobile <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData.phone}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  User ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData.sponsorCode}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  Package <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("package", { required: "Please select a package" })}
                  className="w-full px-4 py-3 rounded-lg border border-[#49c2c7] focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-200 outline-none"
                  disabled={isSubmitting}
                >
                  <option value="">Select Project</option>
                  {packages.length > 0 ? (
                    packages.map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>
                        {pkg.title} - ₹{pkg.price?.toLocaleString() ?? 'N/A'} - {pkg.width} x {pkg.length}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No packages available
                    </option>
                  )}
                </select>
                {errors.package && (
                  <p className="mt-1 text-sm text-red-500">{errors.package.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  Customer <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("customer", { required: "Please select a customer" })}
                  className="w-full px-4 py-3 rounded-lg border border-[#49c2c7] focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-200 outline-none"
                  disabled={isSubmitting}
                >
                  <option value="">Select Customer</option>
                  {customers.length > 0 ? (
                    customers.map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>
                        {pkg.customerName}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No customer available
                    </option>
                  )}
                </select>
                {errors.customer && (
                  <p className="mt-1 text-sm text-red-500">{errors.customer.message}</p>
                )}
              </div>
            </div>

            <div className="flex justify-center pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-gradient-to-r from-[#13407e] via-[#0d61b5] to-[#25b2da] text-white px-8 py-3 rounded-lg 
                hover:from-[#0d61b5] hover:via-[#25b2da] hover:to-[#13407e] transition-all duration-500 transform hover:scale-105 
                focus:ring-4 focus:ring-[#25b2da]/20 shadow-md hover:shadow-lg active:scale-95
                ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Processing...' : 'Send Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BuyPackage;